import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useForm,
  FormProvider,
  Controller,
  useFormState,
} from 'react-hook-form';
import Preloader from 'components/Preloader';
import { updateMenuSettings, getMenusSettings } from 'redux/actions/Menu/menu';
import LabelTooltip from 'components/FormFields/LabelTooltip';
import {
  Grid,
  Button,
  Box,
  Switch,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FormControl from '@mui/material/FormControl';
import CustomTextInput from 'components/Forms/CustomTextInput';
import FormRadio from 'components/Forms/CustomRadioGroup';
import ConfirmPromptModal from 'components/ConfirmPromptModal/ConfirmPromptModal';
import ErrorBoundaryWrapper from 'components/ErrorBoundaryWrapper';
import { useTranslation } from 'react-i18next';
import ConfirmProviderAction from 'components/Confirm/ConfirmProviderAction';
import { useAutoConfirmModal } from 'hooks/useAutoConfirmModal';
import PhoneImage from 'assets/images/PhoneIcon.png';
import {
  deliveryServiceDisabledInfo,
  deliveryServiceInHouseInfo,
  deliveryServiceInfo,
} from '../schema';
import { formatNumber, priceTransform } from 'helpers/formPrice';
import FlatFee from '../FlatFee';
import {
  getAllDeliveryStrategy,
  updateCurrentDeliveryStrategy,
} from 'redux/actions/business';
import StrategyList from 'pages/WebsiteSettings/Dispatch/StrategyList';
import usePermission from 'hooks/permission';
import * as all from 'constants/permissions';

const defaultModel = {
  delivery: false,
  deliveryBy: [
    {
      deliveryService: 'self',
      maxDeliveryFee: null,
      tenantKey: '',
      allowProviderDelivery: false,
      dropoffVerification: {
        leaveAtDoor: {
          enabled: true,
          picture: true,
        },
        meetAtDoor: {
          enabled: true,
          signature: false,
          pinCode: true,
          barCode: false,
        },
      },
    },
  ],
  deliveryFee: null,
  deliveryFlatFeeSettings: {
    flatFeeEnabled: false,
    deliveryRateSettings: [],
  },
  deliveryLimits: {
    maxDeliveryFee: null,
    maxDeliveryRadius: null,
  },
  strategyId: '',
};

const DeliveryService = () => {
  const { t } = useTranslation();
  const { openAutoConfirmModal, handleCloseAutoConfirmModal } =
    useAutoConfirmModal();

  const radioGroupOptions = [
    {
      value: 'self',
      label: t('In-house Delivery'),
      disabled: false,
    },
    {
      value: 'dispatch',
      label: t('AI Dispatch (Recommended)'),
      disabled: false,
    },
    {
      value: 'uber',
      label: t('Uber Delivery'),
      disabled: true,
    },
    {
      value: 'doordash',
      label: t('Doordash Delivery'),
      disabled: true,
    },
    {
      value: 'fetchme',
      label: t('Fetchme Delivery'),
      disabled: true,
    },
    {
      value: 'cartwheel',
      label: t('Cartwheel Delivery'),
      disabled: true,
    },
    {
      value: 'deliverlogic',
      label: t('Deliverlogic'),
      disabled: true,
    },
  ];

  const showInputs = false;

  const dispatch = useDispatch();
  const formRef = useRef();
  const [model, setModel] = useState(defaultModel);
  const checkPermission = usePermission();
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [deliveryService, setDeliveryService] = useState('');
  const [inHouseDelivery, setInHouseDelivery] = useState(false);
  const [strategyList, setStrategyList] = useState([]);
  const [currentSelectedStrategyId, setCurrentSelectedStrategyId] =
    useState('');

  const checkDeliveryService =
    deliveryService === 'uber' ||
    deliveryService === 'doordash' ||
    deliveryService === 'nash' ||
    deliveryService === 'dispatch' ||
    deliveryService === 'deliverlogic' ||
    deliveryService === 'fetchme' ||
    deliveryService === 'cartwheel';

  const currentSchema =
    delivery && !inHouseDelivery
      ? deliveryServiceInfo
      : delivery && inHouseDelivery
      ? deliveryServiceInHouseInfo
      : deliveryServiceDisabledInfo;

  const websiteFormMethods = useForm({
    defaultValues: model,
    mode: 'all',
    resolver: yupResolver(currentSchema),
  });
  const { reset, setValue, getValues } = websiteFormMethods;
  const { isDirty } = useFormState({ control: websiteFormMethods.control });
  const flatEnable = getValues('deliveryFlatFeeSettings.flatFeeEnabled');
  const isHouseDelivery = deliveryService !== 'dispatch';

  useEffect(() => {
    if (Object.keys(model).length) {
      setInHouseDelivery(model?.deliveryBy?.[0]?.deliveryService === 'self');
    }
    return () => {
      dispatch({ type: 'PROVIDERS_START_RESUME_RESET' });
    };
  }, [model]);

  const handleGetMenuSettings = () => {
    setLoading(true);
    return getMenusSettings()
      .then((data) => {
        if (!data.settings) {
          return;
        }
        setDeliveryService(
          (data.settings.deliveryBy &&
            data.settings.deliveryBy[0]?.deliveryService) ||
            'self'
        );
        setDelivery(
          !data.settings.creditCard || !data.settings.payment
            ? false
            : data.settings.delivery
        );

        if (!data.settings.payment) {
          data.settings.creditCard = false;
          data.settings.delivery = false;
        }
        data?.settings?.deliveryFlatFeeSettings?.deliveryRateSettings?.forEach(
          (item) => {
            item.deliveryPrice = formatNumber(item.deliveryPrice || 0);
            item.providerLimitedFee = formatNumber(
              item.providerLimitedFee || 0
            );
          }
        );

        setModel(data.settings);

        return getAllDeliveryStrategy()
          .then((response) => {
            const list = response?.data?.data || [];
            const findDefaultStrategy = list.find((item) => item?.selected);
            const findDefaultStrategyByDefault = list.find(
              (item) => item?.isDefault
            );
            setStrategyList(list);

            setCurrentSelectedStrategyId(
              findDefaultStrategy?._id || findDefaultStrategyByDefault?._id
            );
            reset(
              {
                ...data.settings,
                strategyId:
                  findDefaultStrategy?._id || findDefaultStrategyByDefault?._id,
              },
              { keepDirty: false }
            );
          })
          .catch(() => {
            reset(
              {
                ...data.settings,
              },
              { keepDirty: false }
            );
          });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    handleGetMenuSettings();
  }, []);

  const handleSubmit = (values) => {
    setDisabled(true);
    setLoading(true);
    if (values?.deliveryBy?.[0]?.deliveryService === 'self') {
      values.deliveryFlatFeeSettings['flatFeeEnabled'] = true;
    } else {
      values.deliveryFlatFeeSettings['flatFeeEnabled'] = flatEnable;
    }
    values?.deliveryFlatFeeSettings?.deliveryRateSettings?.forEach((item) => {
      item.deliveryPrice = formatNumber(item.deliveryPrice || 0);
      item.providerLimitedFee = formatNumber(item.providerLimitedFee || 0);
    });
    const websiteSettingsModel = {
      ...model,
      ...values,
      theme: model.theme,
    };
    if (!checkDeliveryService) {
      websiteSettingsModel.deliveryBy[0].maxDeliveryFee = 0;
    }
    if (deliveryService && websiteSettingsModel.deliveryBy.length) {
      websiteSettingsModel.deliveryBy[0].deliveryService = deliveryService;
    }
    delete websiteSettingsModel.payment;
    if (
      values?.deliveryBy?.[0]?.deliveryService === 'dispatch' &&
      checkPermission(all['SUPER'])
    ) {
      updateCurrentDeliveryStrategy({
        strategyId: websiteSettingsModel.strategyId,
      }).then(() => {});
    }
    if (websiteSettingsModel?.strategyId) {
      delete websiteSettingsModel.strategyId;
    }
    dispatch(updateMenuSettings(websiteSettingsModel, 'website'))
      .then((res) => {
        websiteSettingsModel.payment = res?.value?.settings?.payment;
        dispatch({ type: 'PROVIDERS_START_RESUME_RESET' });
        return handleGetMenuSettings();
      })
      .finally(() => {
        setDisabled(false);
      });
  };

  const handleChangeService = (e) => {
    e.persist();
    if (e.target) {
      const nextValue = e.target.value;
      const isSelf = nextValue === 'self';
      setValue('deliveryBy[0].deliveryService', nextValue, {
        shouldDirty: true,
      });
      setValue('deliveryFlatFeeSettings.flatFeeEnabled', isSelf, {
        shouldDirty: true,
      });
      if (
        isSelf &&
        currentSelectedStrategyId &&
        checkPermission(all['SUPER'])
      ) {
        setValue('strategyId', currentSelectedStrategyId, {
          shouldDirty: true,
        });
      }
      setInHouseDelivery(isSelf);
      websiteFormMethods.clearErrors('radius');
      setDeliveryService(nextValue);
    }
  };

  const onChangeAmount = (event) => {
    const { name, value } = event.target;
    const findValue = priceTransform(value);
    setValue(name, findValue, { shouldDirty: true });
  };

  return (
    <ErrorBoundaryWrapper>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ pb: 2 }}>
            <Box sx={{ p: 3 }}>
              <ConfirmPromptModal
                handleConfirm={websiteFormMethods.handleSubmit(handleSubmit)}
                submitData={websiteFormMethods.getValues()}
                hasUnsavedChanges={isDirty}
                formState={websiteFormMethods}
              />
              <ConfirmProviderAction
                handleClose={handleCloseAutoConfirmModal}
                showModal={openAutoConfirmModal}
                title={'website_settings.auto_confirm'}
                description={'website_settings.auto_confirm_desc'}
                label={''}
                icon={PhoneImage}
                iconName={'support phone number'}
                phoneNumber={'website_settings.phoneNumber'}
              />
              <FormProvider {...websiteFormMethods}>
                {loading ? (
                  <Preloader />
                ) : (
                  <form
                    ref={formRef}
                    onSubmit={websiteFormMethods.handleSubmit(
                      handleSubmit,
                      (err) => console.log(err)
                    )}
                  >
                    <Grid container columns={{ xs: 9 }} spacing={2}>
                      <Grid item xs={12}>
                        <Grid item xs={12}>
                          <FormControl
                            onChange={handleChangeService}
                            sx={{
                              width: '100%',
                              '& .MuiFormGroup-root': {
                                display: 'grid',
                                gridTemplateRows: 'repeat(2, auto)',
                                gridAutoFlow: 'column',
                                gridAutoColumns: 'minmax(0, 1fr)',
                                columnGap: 2,
                                width: '100%',
                              },
                              '& .MuiFormControlLabel-root': {
                                width: 'auto !important',
                                m: 0,
                              },
                            }}
                          >
                            <h5 className="mb-2">
                              {t('website_settings.delivery_service')}
                            </h5>
                            <FormRadio
                              customStyles={{}}
                              name="deliveryBy[0].deliveryService"
                              defaultValue={
                                model.deliveryBy?.[0]?.deliveryService || 'self'
                              }
                              options={radioGroupOptions}
                            />
                          </FormControl>
                        </Grid>
                        {showInputs && (
                          <Grid container spacing={2} columns={{ xs: 9 }}>
                            {checkDeliveryService && (
                              <Grid item xs={2} sx={{ height: '74px' }}>
                                <CustomTextInput
                                  sx={{ width: '100%' }}
                                  inputProps={{
                                    type: 'number',
                                    step: 'any',
                                  }}
                                  disabled={true}
                                  onChange={onChangeAmount}
                                  name="deliveryBy[0].maxDeliveryFee"
                                  label={
                                    <LabelTooltip value="Maximum Delivery fee ($)(Optional)" />
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Tooltip
                                          title={'Maximum Delivery fee ($)'}
                                        >
                                          <HelpOutlineIcon
                                            size={'small'}
                                            fontSize={'small'}
                                          />
                                        </Tooltip>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            )}
                            <Grid item xs={2} sx={{ height: '74px' }}>
                              <CustomTextInput
                                sx={{ width: '100%' }}
                                inputProps={{
                                  type: 'number',
                                  step: 'any',
                                }}
                                name="deliveryFee"
                                disabled={true}
                                onChange={onChangeAmount}
                                label={
                                  <LabelTooltip
                                    value={t('website_settings.delivery_fee')}
                                    required
                                  />
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Tooltip
                                        title={t(
                                          'website_settings.flat_rate_fee_creating_this'
                                        )}
                                      >
                                        <HelpOutlineIcon fontSize={'small'} />
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                            <Grid item xs={2} sx={{ height: '74px' }}>
                              <CustomTextInput
                                sx={{ width: '100%' }}
                                inputProps={{
                                  type: 'number',
                                  min: '1',
                                  step: 'any',
                                }}
                                disabled={true}
                                name="radius"
                                label={
                                  <LabelTooltip
                                    value={t(
                                      'website_settings.delivery_radius'
                                    )}
                                    required
                                  />
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Tooltip
                                        title={t(
                                          'website_settings.distance_ willing_to_service'
                                        )}
                                      >
                                        <HelpOutlineIcon fontSize={'small'} />
                                      </Tooltip>
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Grid>

                      {!isHouseDelivery && (
                        <StrategyList
                          strategyList={strategyList}
                          disabled={!checkPermission(all['SUPER'])}
                        />
                      )}
                      <Grid item xs={12} sx={{ pl: 2 }}>
                        <CustomTextInput
                          sx={{ width: '100%', maxWidth: '336px', mt: 1 }}
                          name="deliveryBy[0].tenantKey"
                          label={t('Tenant Key')}
                          disabled
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ pl: 2 }}>
                        <Controller
                          name="deliveryBy.0.allowProviderDelivery"
                          control={websiteFormMethods.control}
                          render={({
                            field: { value, onChange, ref, ...rest },
                          }) => (
                            <FormControlLabel
                              control={
                                <Switch
                                  {...rest}
                                  inputRef={ref}
                                  checked={!!value}
                                  onChange={(e) => onChange(e.target.checked)}
                                  disabled
                                />
                              }
                              label={t('Allow Provider Delivery')}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FlatFee
                          websiteFormMethods={websiteFormMethods}
                          model={model}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          disabled={disabled || !isDirty}
                          variant="contained"
                          color="primary"
                          size="lg"
                          className="px-5"
                          type="submit"
                        >
                          {t('website_settings.update')}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </FormProvider>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </ErrorBoundaryWrapper>
  );
};

export default DeliveryService;
